import React from 'react';
import { Typography, Chip, Box, IconButton, Tooltip } from '@mui/material';
import { Visibility, AttachMoney, DeleteOutline } from '@mui/icons-material';

const getStatusString = (status, t) => {
  switch (status) {
    case 'paid':
      return t('invoiceManagement:status.paid');
    case 'partially_paid':
      return t('invoiceManagement:status.partiallyPaid');
    case 'unpaid':
      return t('invoiceManagement:status.unpaid');
    default:
      return status;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'partially_paid':
      return 'warning';
    case 'unpaid':
      return 'error';
    default:
      return 'default';
  }
};

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString();
};

export const getInvoiceColumns = (onView, onUpdatePayment, onClearInvoice, t) => [
  {
    field: 'sn',
    headerName: t('invoiceManagement:table.sn'),
    width: 70,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'invoice_number',
    headerName: t('invoiceManagement:table.invoiceNumber'),
    width: 150,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976D2' }}>
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'user',
    headerName: t('invoiceManagement:table.user'),
    width: 200,
    renderCell: (params) => (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.row.user?.email || params.row.user?.phone || `@${params.row.user.username}`}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'subject',
    headerName: t('invoiceManagement:table.subject'),
    width: 250,
    renderCell: (params) => (
      <Typography 
        variant="body2" 
        sx={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}
        title={params.value}
      >
        {params.value}
      </Typography>
    ),
  },
  {
    field: 'amount',
    headerName: t('invoiceManagement:table.amount'),
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
        {params && params.value !== undefined && params.value !== null ? `₦${Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
      </Typography>
    ),
  },
  {
    field: 'amount_paid',
    headerName: t('invoiceManagement:table.paid'),
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2" color="success.main">
        {params && params.value !== undefined && params.value !== null ? `₦${Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
      </Typography>
    ),
  },
  {
    field: 'outstanding',
    headerName: t('invoiceManagement:table.outstanding'),
    width: 120,
    renderCell: (params) => (
      <Typography 
        variant="body2" 
        sx={{ 
          fontWeight: 'bold',
          color: params.value > 0 ? 'error.main' : 'success.main'
        }}
      >
        {params && params.value !== undefined && params.value !== null ? `₦${Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : ''}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: t('invoiceManagement:table.status'),
    width: 130,
    renderCell: (params) => (
      <Chip
        label={getStatusString(params.value, t)}
        color={getStatusColor(params.value)}
        size="small"
        sx={{ fontWeight: 'bold' }}
      />
    ),
  },
  {
    field: 'created_at',
    headerName: t('invoiceManagement:table.created'),
    width: 120,
    renderCell: (params) => (
      <Typography variant="body2">
        {formatDate(params.value)}
      </Typography>
    ),
  },
  {
    field: 'actions',
    headerName: t('invoiceManagement:table.actions'),
    width: 165,
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      const canClear = params.row.status === 'unpaid' && Number(params.row.amount_paid || 0) === 0;

      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('invoiceManagement:tooltips.viewDetails')}>
            <IconButton
              size="small"
              onClick={() => onView(params.row)}
              sx={{ color: '#1976D2' }}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('invoiceManagement:tooltips.updatePayment')}>
            <IconButton
              size="small"
              onClick={() => onUpdatePayment(params.row)}
              sx={{ color: '#4CAF50' }}
            >
              <AttachMoney />
            </IconButton>
          </Tooltip>
          <Tooltip title={canClear ? t('invoiceManagement:tooltips.clearInvoice') : t('invoiceManagement:tooltips.clearInvoiceDisabled')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onClearInvoice(params.row)}
                disabled={!canClear}
                sx={{ color: canClear ? 'error.main' : 'action.disabled' }}
              >
                <DeleteOutline />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      );
    },
  },
];

export default getInvoiceColumns;
